import { dedupeForCumulative } from './dedupe';
import { orderQueue } from './leitner';
import { filterToLevel, levelFor } from './ramp';
import { isSingleSessionMode, poolForMode, selectItems, shuffle } from './select';
import type {
  CategoryLevel,
  DifficultyOverride,
  Item,
  Progress,
  Session,
  StudyMode,
} from './types';

export interface QueueInput {
  items: Item[];
  sessions: Session[];
  mode: StudyMode;
  sessionId?: string;
  want: 'flashcard' | 'question';
  progress: Map<string, Progress>;
  levels: Map<string, CategoryLevel>;
  override: DifficultyOverride;
  now: number;
  limit?: number;
  focusLatestClass?: boolean;
  /** Cumulative only: let textbook chapters into the mix as well. */
  includeTextbook?: boolean;
  /** Only this category, for "Practice this" from the weak-spot dashboard. */
  category?: string;
  /**
   * Only these categories. One part of the mid-term paper spans several at
   * once — limiting reactant, percent yield and stoichiometry are a single
   * ten-mark question — so drilling it needs more than one name.
   */
  categories?: readonly string[];
  /** Items already answered in this run, kept out so box 1 does not repeat. */
  excludeIds?: ReadonlySet<string>;
  rng?: () => number;
}

export interface QueueResult {
  items: Item[];
  /** How many were removed as duplicates of a chapter-review item. */
  deduped: number;
  /** True when nothing was due and the queue fell back to everything. */
  usedFallback: boolean;
}

/**
 * Builds what to study next.
 *
 * A single class or chapter review is studied whole and in its imported order —
 * picking a session means wanting all of it. Cumulative and practice queues are
 * the smart ones: de-duplicated, filtered to the category's difficulty level,
 * then ordered by Leitner box and how overdue each item is.
 */
export function buildQueue(input: QueueInput): QueueResult {
  const {
    items,
    sessions,
    mode,
    sessionId,
    want,
    progress,
    levels,
    override,
    now,
    limit,
    category,
    excludeIds = new Set(),
    rng,
  } = input;

  if (isSingleSessionMode(mode)) {
    return {
      items: selectItems(items, { mode, sessionId, want, sessions }),
      deduped: 0,
      usedFallback: false,
    };
  }

  let pool = poolForMode(items, {
    mode: 'cumulative',
    want,
    sessions,
    includeTextbook: input.includeTextbook,
  });
  const wanted = input.categories?.length ? new Set(input.categories) : null;
  if (wanted) pool = pool.filter((i) => wanted.has(i.category));
  else if (category) pool = pool.filter((i) => i.category === category);

  const { kept, dropped } = dedupeForCumulative(pool, sessions);
  pool = kept;

  // The ramp is per category, so filter each category to its own level.
  const byCategory = new Map<string, Item[]>();
  for (const item of pool) {
    const list = byCategory.get(item.category) ?? [];
    list.push(item);
    byCategory.set(item.category, list);
  }
  const levelled: Item[] = [];
  for (const [name, list] of byCategory) {
    levelled.push(...filterToLevel(list, levelFor(name, levels, override)));
  }

  const progressPlain = new Map(progress);
  let due = orderQueue(levelled, progressPlain, now, excludeIds);
  let usedFallback = false;

  // Nothing due is a good problem, but an empty screen is not an answer.
  if (due.length === 0 && levelled.length > 0) {
    usedFallback = true;
    due = shuffle(
      levelled.filter((i) => !excludeIds.has(i.id)),
      rng,
    );
  }

  if (input.focusLatestClass) due = focusLatest(due, sessions);

  return {
    items: limit ? due.slice(0, limit) : due,
    deduped: dropped.size,
    usedFallback,
  };
}

function focusLatest(items: Item[], sessions: Session[]): Item[] {
  const classes = sessions
    .filter((s) => s.kind === 'class' && !s.deleted)
    .sort((a, b) => Date.parse(b.addedAt) - Date.parse(a.addedAt));
  const latest = classes[0]?.id;
  if (!latest) return items;
  return [...items.filter((i) => i.sessionId === latest), ...items.filter((i) => i.sessionId !== latest)];
}
